// shared/src/schemas/qa-api.ts
// S-10 (C035) — runtime Zod guards for the /api/qa/* JSON response shapes the
// dashboard reads. ZoneQa + ZoneQaRuns previously did `r.json() as Promise<T>`
// — a bare widening cast that silently reads `undefined` as real data if the
// server shape drifts. These schemas mirror the hand-written types in
// ../types/qa.ts (question + answer + composite + status) plus the api-worker
// /api/qa/runs row, and are `.passthrough()` so a server that ADDS a field is
// tolerated (only the fields the UI reads are validated). Parsing is fail-loud
// (.parse throws → the query surfaces an error state) per feedback_no_fallbacks.
//
// KEEP IN LOCKSTEP with ../types/qa.ts (QaQuestion/QaAnswer/QaQuestionWithLatest/
// QaStatusRow) and workers/api-worker/src/routes/qa.ts (rowToAnswer/rowToQuestion,
// the /runs + /status mappers).

import { z } from 'zod';

export const QaStatusApiEnum     = z.enum(['idle', 'submitted', 'in_progress', 'updated', 'failed']);
export const QaConfidenceApiEnum = z.enum(['HIGH', 'MED', 'LOW', 'UNKNOWN']);

/** GET /api/qa/questions[/archived] → question half of QaQuestionWithLatest. */
export const QaQuestionApiSchema = z.object({
  id:               z.string(),
  question_text:    z.string(),
  created_at:       z.string(),
  updated_at:       z.string(),
  latest_status:    QaStatusApiEnum,
  latest_status_at: z.string().nullable(),
  latest_brief_id:  z.string().nullable(),
  latest_error_msg: z.string().nullable(),
  display_order:    z.number(),
}).passthrough();

/** The API-shape answer — citations already parsed to a string[] by rowToAnswer
 *  (NOT the DDL `citations_json` string). trajectory_* nullable (legacy rows). */
export const QaAnswerApiSchema = z.object({
  id:                   z.string(),
  brief_id:             z.string(),
  question_id:          z.string(),
  created_at:           z.string(),
  consensus_answer:     z.string(),
  consensus_reasoning:  z.string(),
  contrarian_answer:    z.string(),
  contrarian_reasoning: z.string(),
  trajectory_answer:    z.string().nullable(),
  trajectory_reasoning: z.string().nullable(),
  predicted_answer:     z.string(),
  predicted_reasoning:  z.string(),
  predicted_confidence: QaConfidenceApiEnum,
  citations:            z.array(z.string()),
  tokens_in:            z.number().nullable(),
  tokens_out:           z.number().nullable(),
  cost_usd:             z.number().nullable(),
  llm_call_id:          z.string().nullable(),
}).passthrough();

/** GET /api/qa/questions → QaQuestionWithLatest[]. latest_answer null when no
 *  answer yet OR the question is non-terminal. */
export const QaQuestionWithLatestSchema = z.object({
  question:      QaQuestionApiSchema,
  latest_answer: QaAnswerApiSchema.nullable(),
}).passthrough();

/** GET /api/qa/status → QaStatusRow[] (poll shape; no answer bodies). */
export const QaStatusRowSchema = z.object({
  id:               z.string(),
  latest_status:    QaStatusApiEnum,
  latest_status_at: z.string().nullable(),
  latest_brief_id:  z.string().nullable(),
  latest_error_msg: z.string().nullable(),
}).passthrough();

/** GET /api/qa/runs → the read-only Q&A run-log row (ops-lab ZoneQaRuns).
 *  Promoted from the ZoneQaRuns-local interface (C035: >3 fields, mirrored in
 *  the api-worker /runs mapper). */
export const QaRunRowSchema = z.object({
  created_at:    z.string(),
  question_id:   z.string(),
  status:        z.string(),          // 'success' | 'error'
  duration_ms:   z.number().nullable(),
  cost_usd:      z.number().nullable(),
  tokens_in:     z.number().nullable(),
  tokens_out:    z.number().nullable(),
  brief_id:      z.string().nullable(),
  error_message: z.string().nullable(),
  trigger:       z.string().nullable(),   // 'cron' | 'manual' | null (pre-0034)
}).passthrough();
export type QaRunRow = z.infer<typeof QaRunRowSchema>;

/** POST /api/qa/refresh-all → the client-fanout id list (ZoneQa refresh-all). */
export const QaRefreshAllResponseSchema = z.object({
  submitted: z.array(z.string()),
  brief_id:  z.string(),
  mode:      z.string().optional(),
}).passthrough();
